<template>
  <div class="tour-tooltip" :style="tooltipStyle" :class="['tour-tooltip--' + position, { 'tour-tooltip--congrats': congrats }]">
    <div v-if="position !== 'center'" class="tour-tooltip__arrow" :class="'tour-tooltip__arrow--' + arrowSide"></div>
    <div v-if="!congrats" class="tour-tooltip__progress">
      <div class="tour-tooltip__progress-bar" :style="{ width: progressPercent + '%' }"></div>
    </div>
    <div class="tour-tooltip__header">
      <h4 class="tour-tooltip__title">{{ $t(title) }}</h4>
    </div>
    <p class="tour-tooltip__content">{{ $t(content) }}</p>

    <!-- Final congrats: single thank-you button -->
    <div v-if="congrats && finalCongrats" class="tour-tooltip__footer">
      <button
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('finish')"
      >
        {{ $t('tours.nav.thanks') }}
      </button>
    </div>

    <!-- Congrats: two custom buttons (continue to next tour) -->
    <div v-else-if="congrats" class="tour-tooltip__footer">
      <button
        class="tour-tooltip__btn tour-tooltip__btn--outline"
        @click="$emit('finish')"
      >
        {{ $t('tours.nav.noThanks') }}
      </button>
      <button
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('continue')"
      >
        {{ $t('tours.nav.yes') }}
      </button>
    </div>

    <!-- AwaitClick with optional next: prev + next buttons alongside awaitClick -->
    <div v-else-if="awaitClick && awaitClickOptional" class="tour-tooltip__footer">
      <button
        v-if="!isFirstStep && !hidePrev"
        class="tour-tooltip__btn tour-tooltip__btn--outline"
        @click="$emit('prev')"
      >
        {{ $t('tours.nav.prev') }}
      </button>
      <button
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('next')"
      >
        {{ $t('tours.nav.next') }}
      </button>
    </div>

    <!-- AwaitClick: only prev button when not first step (+ optional skip) -->
    <div v-else-if="awaitClick" class="tour-tooltip__footer">
      <button
        v-if="!isFirstStep && !hidePrev"
        class="tour-tooltip__btn tour-tooltip__btn--outline"
        @click="$emit('prev')"
      >
        {{ $t('tours.nav.prev') }}
      </button>
      <button
        v-if="canSkip"
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('skip')"
      >
        {{ $t('tours.nav.skip') }}
      </button>
    </div>

    <!-- Default navigation -->
    <div v-else class="tour-tooltip__footer">
      <button
        v-if="!isFirstStep && !hidePrev && (!isLastStep || hideFinish)"
        class="tour-tooltip__btn tour-tooltip__btn--outline"
        @click="$emit('prev')"
      >
        {{ $t('tours.nav.prev') }}
      </button>

      <button
        v-if="!isLastStep"
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('next')"
      >
        {{ $t('tours.nav.next') }}
      </button>

      <button
        v-if="isLastStep && !hideFinish"
        class="tour-tooltip__btn tour-tooltip__btn--primary"
        @click="$emit('finish')"
      >
        {{ $t('tours.nav.finish') }}
      </button>
    </div>
  </div>
</template>

<script>
const TOOLTIP_MARGIN = 16
const TOOLTIP_WIDTH = 320

export default {
  name: 'TourTooltip',

  props: {
    title: { type: String, required: true },
    content: { type: String, required: true },
    position: { type: String, default: 'right' },
    spotlightRect: { type: Object, default: null },
    stepIndex: { type: Number, required: true },
    totalSteps: { type: Number, required: true },
    isLastStep: { type: Boolean, default: false },
    isFirstStep: { type: Boolean, default: false },
    hideFinish: { type: Boolean, default: false },
    hidePrev: { type: Boolean, default: false },
    awaitClick: { type: Boolean, default: false },
    awaitClickOptional: { type: Boolean, default: false },
    congrats: { type: Boolean, default: false },
    finalCongrats: { type: Boolean, default: false },
    aboveDrawer: { type: Boolean, default: false },
    canSkip: { type: Boolean, default: false }
  },

  computed: {
    progressPercent() {
      if (this.totalSteps <= 1) return 100
      return Math.round(((this.stepIndex + 1) / this.totalSteps) * 100)
    },

    arrowSide() {
      const map = { right: 'left', left: 'right', top: 'bottom', bottom: 'top' }
      return map[this.position] || 'left'
    },

    tooltipStyle() {
      if (!this.spotlightRect) {
        return { display: 'none' }
      }

      const r = this.spotlightRect
      const style = { width: TOOLTIP_WIDTH + 'px' }

      switch (this.position) {
        case 'center':
          style.left = '50%'
          style.top = '50%'
          style.transform = 'translate(-50%, -50%)'
          break
        case 'right':
          style.left = (r.right + TOOLTIP_MARGIN) + 'px'
          style.top = (r.top + r.height / 2) + 'px'
          style.transform = 'translateY(-50%)'
          break
        case 'left':
          style.left = (r.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN) + 'px'
          style.top = (r.top + r.height / 2) + 'px'
          style.transform = 'translateY(-50%)'
          break
        case 'top':
          style.left = (r.left + r.width / 2) + 'px'
          style.top = (r.top - TOOLTIP_MARGIN) + 'px'
          style.transform = 'translate(-50%, -100%)'
          break
        case 'bottom':
          style.left = (r.left + r.width / 2) + 'px'
          style.top = (r.bottom + TOOLTIP_MARGIN) + 'px'
          style.transform = 'translateX(-50%)'
          break
      }

      if (this.aboveDrawer) {
        style.zIndex = '10003'
      }

      return style
    }
  }
}
</script>

<style scoped>
.tour-tooltip {
  position: fixed;
  z-index: 9993;
  pointer-events: auto;
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: 'avenirnext', 'montserrat-regular', Helvetica, Roboto, Arial, sans-serif;
}

/* Arrow */
.tour-tooltip__arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
}

.tour-tooltip__arrow--left {
  left: -6px;
  top: 50%;
  margin-top: -6px;
}

.tour-tooltip__arrow--right {
  right: -6px;
  top: 50%;
  margin-top: -6px;
}

.tour-tooltip__arrow--top {
  top: -6px;
  left: 50%;
  margin-left: -6px;
}

.tour-tooltip__arrow--bottom {
  bottom: -6px;
  left: 50%;
  margin-left: -6px;
}

/* Progress bar */
.tour-tooltip__progress {
  width: 100%;
  height: 4px;
  background: #f1edf5;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}

.tour-tooltip__progress-bar {
  height: 100%;
  background: #6B1F4D;
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Header */
.tour-tooltip__header {
  margin-bottom: 8px;
}

.tour-tooltip__title {
  font-size: 15px;
  font-weight: 600;
  color: #2f1937;
  margin: 4px 0 0 0;
}

/* Content */
.tour-tooltip__content {
  font-size: 13px;
  color: #555e75;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

/* Footer */
.tour-tooltip__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.tour-tooltip__btn {
  height: 35px;
  border-radius: 6px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border: none;
  outline: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.tour-tooltip__btn--primary {
  background-color: #6B1F4D;
  border: 1.6px solid #6B1F4D;
  color: #fff;
}

.tour-tooltip__btn--primary:hover {
  background-color: #552040;
}

.tour-tooltip__btn--outline {
  background: transparent;
  border: 1.6px solid #6B1F4D;
  color: #6B1F4D;
}

.tour-tooltip__btn--outline:hover {
  background-color: #6B1F4D;
  color: #fff;
}

/* Congrats variant */
.tour-tooltip--congrats {
  text-align: center;
  padding: 28px 24px;
}

.tour-tooltip--congrats .tour-tooltip__title {
  font-size: 18px;
}

.tour-tooltip--congrats .tour-tooltip__footer {
  justify-content: center;
  margin-top: 4px;
}
</style>
